import type { NullableGrantEntry } from "../../../shared/rbac/roles";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  canAuthorizeVisitors,
  isDraft,
} from "../../../shared/connectors/isDraft";
import { configurationPatch } from "../../../shared/connectors/configurationPatch";
import { isManagedAiConnector } from "../../../shared/connectors/managedAi";
import { withoutListCatalogs } from "../../../shared/connectors/listRows";
import { sharedMutators } from "../../../shared/mutators";
import { GRANT_REQUEST_NOTE_MAX_LENGTH } from "../../../shared/grantRequests";
import { DeclineGrantRequestModal } from "../DeclineGrantRequestModal";
import { RequestShareModal } from "../RequestShareModal";
import { NoteInput } from "../NoteInput";
import {
  beginConnectorDestinationEdit,
  waitForConnectorEdits,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { queries } from "../../../shared/queries";
import type { GrantEntry } from "../../../shared/rbac/roles";
import { diffGrants } from "../../../shared/rbac/validation/grantContent";
import { useHasAccess } from "../../hooks/useHasAccess";
import {
  connectorVariants,
  resolveConnectorConfig,
} from "../../lib/connectorDirectory";
import { connectorRawTypeLabel } from "../../lib/connector";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { AccessMapPanel } from "../AccessMapPanel";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { ConnectorStatusPill } from "../ConnectorStatusPill";
import { Button } from "../Button";
import { Banner } from "../Banner";
import { ConfirmModal } from "../ConfirmModal";
import { DeleteConnectorModalBody } from "../Connectors/DeleteConnectorModal";
import { DangerSection } from "../Settings/DangerSection";
import { Modal } from "../Modal";
import { ModalContent, ModalFooter } from "../ModalCard";
import { useScopeAccessCloseWarning } from "../ScopeAccessCloseWarning";
import { GuideToggle } from "../Settings/Guide";
import { TabbedModal } from "../TabbedModal";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { AccessTab } from "./AccessTab";
import { Form, isMcpToolsField, isModelAccessField } from "./Form";
import { ModelAccessField } from "./ModelAccessField";
import { ResourceListField } from "./ResourceListField";
import { SecureBadge } from "./SecureBadge";
import { UsageTab } from "./UsageTab";
import { useConnectorAuthorize } from "./useConnectorAuthorize";
import { VariantPicker } from "./VariantPicker";
import { VisitorAuthorizationSection } from "./VisitorAuthorizationSection";

type Tab = "usage" | "settings" | "access";

const TABS = [
  { key: "settings", label: "Settings", icon: "settings-2" },
  { key: "access", label: "Access", icon: "lock" },
  { key: "usage", label: "Usage", icon: "chart-no-axes-column" },
] as const;

type Props = {
  id: string;
  onClose: () => void;
};

export function ConnectorModal({ id, onClose }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [appRow, appResult] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: id })
  );
  // Server hydration can momentarily drop the row while grants and relations
  // land across syncs; hold the last seen row so the modal never blanks.
  const [heldApp, setHeldApp] = useState<{
    id: string;
    app: NonNullable<typeof appRow>;
  } | null>(null);
  if (appRow && heldApp?.app !== appRow) setHeldApp({ id, app: appRow });
  const app = appRow ?? (heldApp?.id === id ? heldApp.app : undefined);
  const appRef = useRef(app);
  useLayoutEffect(() => {
    appRef.current = app;
  });

  const tenantId = app?.tenantId;
  useEffect(() => {
    if (tenantId) return beginConnectorDestinationEdit(tenantId, id);
  }, [tenantId, id]);

  const appConfig = resolveConnectorConfig(app?.identifier);
  const hasSettings = appConfig != null;
  const mcpToolsField = appConfig?.definition.steps
    .flatMap((step) => step.items)
    .find(isMcpToolsField);
  const modelAccessField = appConfig?.definition.steps
    .flatMap((step) => step.items)
    .find(isModelAccessField);

  const draft = !app || isDraft(app);

  const { tab } = useCurrentUrl(routes.modal.connector, { id });
  const rawTab = tab as Tab;
  const activeTab: Tab =
    !hasSettings && rawTab === "settings" ? "usage" : rawTab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.connector, { id, tab: next });
  const [visitedAccess, setVisitedAccess] = useState(activeTab === "access");
  if (activeTab === "access" && !visitedAccess) setVisitedAccess(true);
  const canWrite = useHasAccess("connected_app:write", app?.id);
  const canShare = useHasAccess("connected_app:share", app?.id);

  // The fetched model catalog is scratch for picking, persisted only so the
  // async fetch can sync it to the client; drop it when the modal closes so it
  // doesn't linger in stored config. Skipped on the paths that remove the
  // connector, since there'd be no row left to write. This can't guarantee a
  // clean picker on reopen — a refresh in flight at close lands afterward and
  // re-adds catalog rows — so the reopen guarantee lives in the open clear below.
  const catalogCleanup = useRef<{
    id: string;
    name: string;
    identifier: string;
    urlPattern: string;
    configuration: Record<string, string>;
    canWrite: boolean;
  } | null>(null);
  const skipCatalogCleanup = useRef(false);
  useEffect(() => {
    if (!app) return;
    catalogCleanup.current = {
      id: app.id,
      name: app.name,
      identifier: app.identifier,
      urlPattern: app.urlPattern,
      configuration: app.configuration,
      canWrite,
    };
  });
  useEffect(
    () => () => {
      const latest = catalogCleanup.current;
      if (
        skipCatalogCleanup.current ||
        !latest?.canWrite ||
        !latest.name.trim()
      )
        return;
      const configuration = withoutListCatalogs(latest.configuration);
      if (
        Object.keys(configuration).length ===
        Object.keys(latest.configuration).length
      )
        return;
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: latest.id,
          configurationPatch: configurationPatch(
            latest.configuration,
            configuration
          ),
        })
      );
    },
    [z]
  );
  // Strip any leftover catalog once when the connector opens, before this
  // session fetches its own — the race-free point to guarantee a clean picker,
  // regardless of a refresh that landed after a previous close.
  const catalogClearedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!app || !canWrite || !app.name.trim()) return;
    if (catalogClearedFor.current === app.id) return;
    catalogClearedFor.current = app.id;
    const configuration = withoutListCatalogs(app.configuration);
    if (
      Object.keys(configuration).length ===
      Object.keys(app.configuration).length
    )
      return;
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        configurationPatch: configurationPatch(
          app.configuration,
          configuration
        ),
      })
    );
  }, [app, canWrite, z]);
  const [guideOn, setGuideOn] = useState(draft);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [authorizePrompt, setAuthorizePrompt] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const fieldSaveErrors = useRef(new Map<string, ReadonlySet<string>>());
  const [shownFieldSaveErrors, setShownFieldSaveErrors] = useState(
    new Map<string, ReadonlySet<string>>()
  );
  const [currentUser] = useQuery(queries.currentUser());

  // Secret values never replicate back to the client, so which masked fields
  // were filled this session is tracked here — the modal outlives the tab
  // switches that unmount the settings form. Switching variant clears the
  // row's secrets, so forget them then too.
  const [locallySet, setLocallySet] = useState<ReadonlySet<string>>(new Set());
  const [requestGrants, setRequestGrants] = useState<GrantEntry[] | null>(null);
  // Whether a reviewer has actually changed the proposed grants. Approval only
  // sends its edited set when this is true, so an unedited (or not-yet-seeded)
  // approval falls back to the stored proposal rather than an empty/stale set.
  const [requestGrantsEdited, setRequestGrantsEdited] = useState(false);
  const [composingRequest, setComposingRequest] = useState<{
    grants: GrantEntry[];
  } | null>(null);
  const [decliningRequest, setDecliningRequest] = useState<string | null>(null);
  const onFieldSet = (key: string) => {
    setLocallySet((prev) => (prev.has(key) ? prev : new Set([...prev, key])));
  };
  const identifier = app?.identifier;
  const fieldKey = `${id}:${identifier}`;
  const onFieldSaveStatus = (field: string, saved: boolean) => {
    const next = new Map(fieldSaveErrors.current);
    const fields = new Set(next.get(fieldKey));
    if (saved) fields.delete(field);
    else fields.add(field);
    if (fields.size) next.set(fieldKey, fields);
    else next.delete(fieldKey);
    fieldSaveErrors.current = next;
    setShownFieldSaveErrors(next);
  };
  // 3B supplies a managed AI connector’s credential and skips its connection
  // test, so its short description always shows, and the guide toggle,
  // secure-storage badge, and test status don’t apply.
  const managedAi = !!identifier && isManagedAiConnector(identifier);
  // Switching connector or variant clears the session-local secret and grant
  // state; adjusted during render so the old connector's values never paint.
  const [resetKey, setResetKey] = useState<string>(`${id}:${identifier}`);
  if (resetKey !== `${id}:${identifier}`) {
    setResetKey(`${id}:${identifier}`);
    setLocallySet(new Set());
    setRequestGrants(null);
    setRequestGrantsEdited(false);
    setComposingRequest(null);
    setDecliningRequest(null);
  }

  const auth = useConnectorAuthorize({
    app,
    definition: appConfig?.definition,
    locallySet,
    onEdit: () => setIsDirty(true),
  });

  const [pendingRequest, pendingRequestResult] = useQuery(
    queries.grantRequestForScope({ scopeId: id })
  );
  const canRequestShare = canWrite && !canShare;
  const isRequester =
    pendingRequest != null &&
    pendingRequest.requestedByUserId === currentUser?.id;
  const showPendingReview = pendingRequest != null;
  const showCompose = canRequestShare && pendingRequest == null;
  const requestMode = showPendingReview || showCompose;

  // Seed the local grant set once: from the pending request's proposal if one
  // exists, otherwise from the connector's current grants (the requester's own),
  // so the submitted request carries the full desired set and approving it via
  // patchScopeGrants doesn't strip the creator's access. Wait for the request
  // query to finish so a not-yet-synced relation can't seed an empty set.
  if (
    requestMode &&
    requestGrants === null &&
    !(pendingRequest && pendingRequestResult.type !== "complete")
  ) {
    const source: readonly Partial<NullableGrantEntry>[] = pendingRequest
      ? pendingRequest.requestedGrants
      : (app?.roleGrantsAsScope ?? []);
    setRequestGrants(
      source
        .map((g) => ({
          userGroupId: g.userGroupId ?? undefined,
          userId: g.userId ?? undefined,
          serviceAccountId: g.serviceAccountId ?? undefined,
          builtinRoleId: g.builtinRoleId ?? undefined,
          customRoleId: g.customRoleId ?? undefined,
        }))
        .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
    );
  }

  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const autoFocusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!draft || autoFocusedFor.current === id) return;
    const body = bodyRef.current;
    if (!body) return;
    requestAnimationFrame(() => {
      const input = body.querySelector<HTMLInputElement>(
        'input[type="text"]:not([readonly])'
      );
      if (input) {
        input.focus({ preventScroll: true });
        autoFocusedFor.current = id;
      }
    });
  }, [draft, id]);

  if (!app) {
    if (appResult?.type !== "complete") {
      return (
        <Modal
          open
          onClose={onClose}
          style={{
            width: 680,
            height: 760,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <WavyLine />
        </Modal>
      );
    }
    return null;
  }

  if (!hasSettings && draft) {
    return null;
  }

  const unsaved = canWrite && app.fresh;

  const grantType = appConfig?.definition.oauth?.grantType(app.configuration);
  const clientCredentials = grantType === "client_credentials";
  const passwordGrant = grantType === "password";
  const visitorAuthorizationSupported = canAuthorizeVisitors(app);
  const showVisitorAuthorization =
    app.authorizationMode === "visitor" || visitorAuthorizationSupported;

  const trimmedName = app.name?.trim();
  const typeLabel = connectorRawTypeLabel(app);
  const showType = Boolean(trimmedName) && trimmedName !== typeLabel;

  const discardDraft = () => {
    skipCatalogCleanup.current = true;
    z.mutate(
      sharedMutators.tidyUpFreshConnectedApp({
        connectedAppId: app.id,
      })
    );
    onClose();
  };

  const dismissModal = () => {
    if (!unsaved) {
      onClose();
      return;
    }
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    discardDraft();
  };

  // reportValidity can't flash its hint on a field in a hidden tab.
  const formIsValid = () => {
    if (hasSettings && activeTab !== "settings") {
      flushSync(() => setActiveTab("settings"));
    }
    const valid =
      bodyRef.current?.querySelector("form")?.reportValidity() ?? true;
    return valid && !fieldSaveErrors.current.get(fieldKey)?.size;
  };

  const publishAndClose = async () => {
    const pending = waitForConnectorEdits(app.tenantId, app.id);
    if (pending) await pending;
    if (!formIsValid()) return false;
    const result = await z.mutate(
      sharedMutators.publishConnectedApp({ connectedAppId: app.id })
    ).server;
    if (result.type !== "success") return false;
    onClose();
    return true;
  };

  const saveAndClose = async () => {
    const pending = waitForConnectorEdits(app.tenantId, app.id);
    if (pending) await pending;
    if (!formIsValid()) return false;
    const currentApp = appRef.current ?? app;
    const name = currentApp.name?.trim() ? currentApp.name : fallbackName();
    const urlPattern =
      currentApp.urlPattern || fallbackUrlPattern(currentApp.configuration);

    if (name !== currentApp.name || urlPattern !== currentApp.urlPattern) {
      const result = await z.mutate(
        sharedMutators.updateConnectedApp(
          await withConnectorDestinationProof(currentApp, {
            name,
            urlPattern,
          })
        )
      ).server;
      if (result.type !== "success") return false;
    }
    if (auth.canAuthorize) {
      // Client-credentials connections need no consent step, so connect in
      // the background; only flows that open a popup get the prompt.
      if (clientCredentials) {
        auth.authorize();
      } else if (!passwordGrant) {
        setAuthorizePrompt(true);
        return false;
      }
    }
    return publishAndClose();
  };

  const authorizeAndClose = async () => {
    setAuthorizePrompt(false);
    if ((await auth.authorize()) === "error") return;
    await publishAndClose();
  };

  const fallbackName = () => {
    const firstName = currentUser?.name?.split(" ")[0] || currentUser?.email;
    return firstName && app.identifier === "apiKey" && appConfig
      ? `${firstName}'s ${appConfig.name}`
      : (appConfig?.name ?? app.name);
  };

  const fallbackUrlPattern = (configuration = app.configuration) =>
    appConfig?.definition.defaultUrlPattern(configuration ?? {}) ?? "";

  // A fresh draft discards on close but must validate on save; a saved
  // connector is only worth checking once the user has actually edited it.
  // Validation runs after staged grants resolve, since switching to the
  // settings tab unmounts the access table and drops its selection.
  const close = () =>
    leave(async () => {
      const pending = waitForConnectorEdits(app.tenantId, app.id);
      if (pending) await pending;
      if (canWrite && fieldSaveErrors.current.get(fieldKey)?.size) return;
      if (canWrite && (isDirty || pending) && !app.fresh && !formIsValid())
        return;
      dismissModal();
    });
  const finish = () =>
    leave(async () => {
      const pending = waitForConnectorEdits(app.tenantId, app.id);
      if (pending) await pending;
      if (canWrite && fieldSaveErrors.current.get(fieldKey)?.size) return;
      if (canWrite && (isDirty || app.fresh || pending) && !formIsValid())
        return;
      if (canWrite && app.fresh) await saveAndClose();
      else dismissModal();
    });

  const composeRequest = (grants: GrantEntry[]) => {
    if (app.fresh && !formIsValid()) return;
    setComposingRequest({ grants });
  };
  // A requester’s added selections only exist in the unsent request, so adding
  // them on the way out continues into sending it instead of closing.
  const leave = (proceed: () => void) =>
    showCompose
      ? requestClose(
          (grants) => (grants ? composeRequest(grants) : proceed()),
          "continue"
        )
      : requestClose(proceed);

  const goToAccess = async () => {
    const pending = waitForConnectorEdits(app.tenantId, app.id);
    if (pending) await pending;
    if (formIsValid()) setActiveTab("access");
  };

  // A reviewer who can share the connector may edit the proposed roles (remove,
  // change, or add) before approving — the same authority that gates Approve.
  // The requester viewing their own pending request stays read-only (they
  // cancel and re-request instead).
  const accessCanManage =
    showCompose ||
    (!requestMode && canShare) ||
    (showPendingReview && !isRequester && canShare);

  // Flag the principals the request would ADD (in the proposed set but not in the
  // connector's current grants) with a "New" badge — so both the requester
  // composing and the reviewer approving see the delta, not one flat list.
  const newPrincipalIds = (() => {
    if (!requestMode) return undefined;
    const current = new Set(
      (app.roleGrantsAsScope ?? []).flatMap((g) =>
        [g.userId, g.userGroupId, g.serviceAccountId].filter(
          (x): x is string => x != null
        )
      )
    );
    return new Set(
      (requestGrants ?? [])
        .map((g) => g.userId ?? g.userGroupId ?? g.serviceAccountId)
        .filter((id): id is string => id != null && !current.has(id))
    );
  })();

  const requestDelta = diffGrants(
    app.roleGrantsAsScope ?? [],
    requestGrants ?? []
  );
  const proposesShare = requestDelta.toInsert.length > 0;

  const submitRequest = async (
    grants: GrantEntry[],
    note: string | undefined
  ) => {
    const pending = waitForConnectorEdits(app.tenantId, app.id);
    if (pending) await pending;
    if (app.fresh && !formIsValid()) return;
    const currentApp = appRef.current ?? app;
    const name = currentApp.name?.trim() ? currentApp.name : fallbackName();
    const urlPattern =
      currentApp.urlPattern || fallbackUrlPattern(currentApp.configuration);
    if (
      app.fresh &&
      (name !== currentApp.name || urlPattern !== currentApp.urlPattern)
    ) {
      const result = await z.mutate(
        sharedMutators.updateConnectedApp(
          await withConnectorDestinationProof(currentApp, {
            name,
            urlPattern,
          })
        )
      ).server;
      if (result.type !== "success") return;
    }
    if (app.fresh) {
      const result = await z.mutate(
        sharedMutators.publishConnectedApp({ connectedAppId: app.id })
      ).server;
      if (result.type !== "success") return;
    }
    const result = await z.mutate(
      sharedMutators.requestScopeRoleGrants({
        requestId: nanoid(12),
        scopeId: app.id,
        note,
        grants: grants.map((g) => ({
          userGroupId: g.userGroupId,
          userId: g.userId,
          serviceAccountId: g.serviceAccountId,
          builtinRoleId: g.builtinRoleId,
          customRoleId: g.customRoleId,
        })),
      })
    ).server;
    if (result.type === "success") onClose();
  };

  const cancelRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.cancelGrantRequest({ requestId: pendingRequest.id })
      );
    }
    onClose();
  };

  const approveRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.approveGrantRequest({
          requestId: pendingRequest.id,
          // Only send the reviewer's edits; an untouched approval omits grants
          // so the server applies the stored proposal as submitted.
          ...(requestGrantsEdited
            ? {
                grants: (requestGrants ?? []).map((g) => ({
                  userGroupId: g.userGroupId,
                  userId: g.userId,
                  serviceAccountId: g.serviceAccountId,
                  builtinRoleId: g.builtinRoleId,
                  customRoleId: g.customRoleId,
                })),
              }
            : {}),
        })
      );
    }
    onClose();
  };

  const requesterName =
    pendingRequest?.requestedByUser?.name ||
    pendingRequest?.requestedByUser?.email ||
    "someone";

  return (
    <>
      <TabbedModal
        icon={
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            hoverBackground={false}
          >
            <ConnectorLogo app={app} size={16} />
          </ConnectorLogoContainer>
        }
        title={trimmedName || typeLabel}
        subtitle={showType ? typeLabel : undefined}
        tabs={TABS.filter(
          (t) =>
            (t.key !== "settings" || hasSettings) &&
            (t.key !== "usage" || !draft)
        ).map((t) => ({
          ...t,
          to: makeUrl(routes.modal.connector, { id, tab: t.key }),
        }))}
        activeTab={activeTab}
        headerRight={
          activeTab === "settings" &&
          !managedAi && (
            <GuideToggle
              isOn={guideOn}
              onToggle={() => setGuideOn((was) => !was)}
              disabled={!canWrite}
            />
          )
        }
        onClose={close}
        footerLeft={!managedAi && <SecureBadge />}
        footerRight={
          <>
            {!showPendingReview && !managedAi && (
              <ConnectorStatusPill app={app} showDetails />
            )}
            {requestMode ? (
              showPendingReview ? (
                isRequester ? (
                  <>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Pending approval
                    </Text>
                    <Button variant="subtle" size="l" onClick={cancelRequest}>
                      Cancel request
                    </Button>
                  </>
                ) : (
                  <>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      Requested by {requesterName}
                    </Text>
                    <Button
                      variant="subtle"
                      size="l"
                      onClick={() => setDecliningRequest(pendingRequest.id)}
                    >
                      Decline
                    </Button>
                    <Button variant="primary" size="l" onClick={approveRequest}>
                      Approve
                    </Button>
                  </>
                )
              ) : proposesShare ? (
                <Button
                  variant="primary"
                  size="l"
                  onClick={() =>
                    requestClose(
                      (grants = requestGrants ?? []) => composeRequest(grants),
                      "continue"
                    )
                  }
                >
                  Request to share
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="l"
                  onClick={finish}
                  disabled={auth.isPending}
                >
                  {canWrite && app.fresh ? "Save" : "Done"}
                </Button>
              )
            ) : canWrite && app.fresh && !visitedAccess ? (
              <Button variant="primary" size="l" onClick={goToAccess}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                size="l"
                onClick={finish}
                disabled={auth.isPending}
              >
                {canWrite && app.fresh ? "Save" : "Done"}
              </Button>
            )}
          </>
        }
      >
        <div ref={bodyRef} onChange={() => setIsDirty(true)}>
          {activeTab === "usage" ? (
            <UsageTab
              connectedAppId={id}
              loading={appResult?.type !== "complete"}
              usageMeasures={appConfig?.definition.usage?.measures ?? null}
              managedAi={managedAi}
            />
          ) : activeTab === "access" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {!app.fresh && (
                <AccessMapPanel
                  nodeId={app.id}
                  noun="connector"
                  guard={(go) =>
                    requestClose(() => {
                      if (canWrite && isDirty && !formIsValid()) return;
                      go();
                    })
                  }
                />
              )}
              <AccessTab
                app={app as unknown as Parameters<typeof AccessTab>[0]["app"]}
                canManage={accessCanManage}
                scopeReady={appResult.type === "complete"}
                canManagePersonalUse={canShare}
                stagedGrantsRef={stagedGrantsRef}
                onEdit={() => setIsDirty(true)}
                localGrants={requestMode ? (requestGrants ?? []) : undefined}
                onChangeLocalGrants={
                  requestMode
                    ? (grants) => {
                        setRequestGrants(grants);
                        setRequestGrantsEdited(true);
                      }
                    : undefined
                }
                newPrincipalIds={newPrincipalIds}
                requestReason={
                  pendingRequest?.note ? (
                    <NoteInput
                      label="Request reason"
                      helperText="Included with this share request."
                      maxLength={GRANT_REQUEST_NOTE_MAX_LENGTH}
                      value={pendingRequest.note}
                      readOnly
                    />
                  ) : null
                }
              />
            </div>
          ) : hasSettings && appConfig ? (
            <>
              {app.authorizationMode === "visitor" &&
                !visitorAuthorizationSupported && (
                  <Banner>
                    This connector does not support individual accounts. Turn
                    off “Require each person to connect their own account” to
                    edit its configuration.
                  </Banner>
                )}
              {canWrite &&
                app.fresh &&
                connectorVariants(appConfig).length > 1 && (
                  <VariantPicker
                    appId={app.id}
                    tenantId={app.tenantId}
                    appConfig={appConfig}
                  />
                )}
              {/* Keyed by variant so switching remounts the form's uncontrolled
                  inputs — a secret typed for the old variant would otherwise
                  linger on screen after the row's secrets are cleared. */}
              <Form
                key={appConfig.identifier}
                definition={appConfig.definition}
                showInstructions={managedAi || guideOn}
                app={app}
                disabled={!canWrite}
                onEdit={() => setIsDirty(true)}
                fieldSaveErrors={
                  shownFieldSaveErrors.get(fieldKey) ?? new Set()
                }
                onFieldSaveStatus={onFieldSaveStatus}
                locallySet={locallySet}
                onFieldSet={onFieldSet}
                auth={auth}
              />
              {mcpToolsField && (
                <ResourceListField
                  item={mcpToolsField}
                  app={app}
                  disabled={!canWrite}
                  onEdit={() => setIsDirty(true)}
                />
              )}
              {modelAccessField && (
                <ModelAccessField
                  item={modelAccessField}
                  app={app}
                  disabled={!canWrite}
                  onEdit={() => setIsDirty(true)}
                />
              )}
              {showVisitorAuthorization && (
                <VisitorAuthorizationSection
                  appId={app.id}
                  providerName={appConfig.definition.name}
                  authorizationMode={app.authorizationMode}
                  grants={app.visitorGrants ?? []}
                  loading={appResult?.type !== "complete"}
                  canWrite={canWrite}
                />
              )}
              {!draft && canWrite && (
                <DangerSection
                  style={{ marginTop: 16 }}
                  title="Delete connector"
                  description="Permanently deletes this connector and its configuration. Any steps that use it will need a replacement."
                  actionLabel="Delete connector"
                  renderConfirm={({ open, onClose: closeConfirm }) =>
                    open ? (
                      <Modal open onClose={closeConfirm} alert>
                        <DeleteConnectorModalBody
                          appId={app.id}
                          onClose={closeConfirm}
                          onDeleted={() => {
                            skipCatalogCleanup.current = true;
                            onClose();
                          }}
                        />
                      </Modal>
                    ) : null
                  }
                />
              )}
            </>
          ) : null}
        </div>
      </TabbedModal>
      {composingRequest && (
        <RequestShareModal
          connectorName={app.name?.trim() ? app.name : fallbackName()}
          onClose={() => setComposingRequest(null)}
          onSend={(note) => submitRequest(composingRequest.grants, note)}
        />
      )}
      {decliningRequest && (
        <DeclineGrantRequestModal
          key={decliningRequest}
          requestId={decliningRequest}
          connectorName={app.name}
          onClose={() => setDecliningRequest(null)}
          onDeclined={onClose}
        />
      )}
      {closeWarningModal}
      {authorizePrompt && appConfig && (
        <Modal open onClose={() => setAuthorizePrompt(false)} alert>
          <ModalContent style={{ width: 360 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Text variant="text-regular-md" color="var(--text-primary)">
                Authorize now?
              </Text>
              <Text variant="text-regular" color="var(--text-secondary)">
                This connector will be saved as a draft until it’s authorized
                with {appConfig.definition.name}.
              </Text>
            </div>
            <ModalFooter>
              <Button
                size="l"
                variant="secondary"
                onClick={() => {
                  setAuthorizePrompt(false);
                  publishAndClose();
                }}
              >
                Save as draft
              </Button>
              <Button size="l" onClick={authorizeAndClose}>
                Authorize
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardDraft}
        title="Discard new connection?"
        description="Your changes will be lost if you continue."
        confirmLabel="Discard"
        destructive
      />
    </>
  );
}
